/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import PreAuthTopBar from 'components/global/PreAuthTopBar';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs';
import TextField from 'components/global/FormTextField';
import PasswordField from 'components/global/PasswordField';
import { fetchUser, updateUser } from 'actions/user';
import { setAppLocale } from 'actions/misc';
import LocaleSelectMini from 'components/global/LocaleSelectMini';
import { COUNTRY, OTHER_COUNTRY_URL } from '../../environment';
import { signinUser } from '../../actions/auths';
import isAdmin from '../../utils/isAdmin';
import Spinner from '../../../images/global/spinner.svg';
import { setDefaultTimezone } from '../../utils/timezone';

/* eslint-disable no-shadow */
const SignIn = ({
  authenticated = false,
  // eslint-disable-next-line camelcase, no-unused-vars
  two_factor = false,
  signinUser,
  fetchUser,
  updateUser,
  errorMessage = '',
  locale,
}) => {
  const history = useHistory();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();
  const [pending, setPending] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (authenticated) {
      history.push('/dashboard');
    }
  }, [authenticated, history]);

  const updateLocale = user => {
    const localeInStorage = localStorage.getItem('locale');
    if (localeInStorage !== user.locale) {
      updateUser({ locale: localeInStorage }, () => window.location.reload());
    }
  };

  const onSubmit = async values => {
    const formValues = { ...values, source: 'practice' };
    setPending(true);
    signinUser(
      formValues,
      res => {
        if (res?.data?.two_factor_id) {
          history.push('/two_factor', {
            id: res.data.two_factor_id,
            email: formValues.email,
          });
        } else {
          fetchUser(userRes => {
            const user = userRes.data;
            if (isAdmin(user.roles)) {
              history.push('/admin');
            } else {
              history.push('/dashboard');
            }
            dayjs.locale(user.locale);
            setDefaultTimezone(user);
            updateLocale(user);
          });
        }
      },
      () => {
        setPending(false);
      },
    );
  };

  const renderCountrySwitch = () => {
    const canadian = (
      <FormattedMessage
        defaultMessage="You are on the Canadian version of the GreenShield Health Practitioner Portal. If you are an American Practitioner, <a>visit the American version of the Practitioner Portal.</a>"
        values={{
          a: address => (
            <a href={OTHER_COUNTRY_URL} className="link">
              {address}
            </a>
          ),
        }}
      />
    );

    const american = (
      <FormattedMessage
        defaultMessage="You are on the American version of the GreenShield Health Practitioner Portal. If you are a Canadian Practitioner, <a>visit the Canadian version of the Practitioner Portal.</a>"
        values={{
          a: address => (
            <a href={OTHER_COUNTRY_URL} className="link">
              {address}
            </a>
          ),
        }}
      />
    );

    return <p>{COUNTRY === 'CA' ? canadian : american}</p>;
  };

  const renderAlert = () => {
    if (errorMessage) {
      return <div className="server-error">{errorMessage}</div>;
    }
    return null;
  };

  const renderSignInButton = () => {
    const label = pending ? (
      <span className="wrapper">
        <span className="spinner">
          <Spinner style={{ width: 24, height: 24, fill: '#ffffff' }} />
        </span>
        <span className="label">
          <FormattedMessage defaultMessage="SIGN IN" />
        </span>
      </span>
    ) : (
      <FormattedMessage defaultMessage="SIGN IN" />
    );

    return (
      <button
        className={`signin-button ${pending ? 'spinner-button' : ''}`}
        type="submit"
        disabled={isSubmitting || pending}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <PreAuthTopBar locale={locale} />
      <div className="signin-container">
        <div className="row">
          <div className="col" style={{ minWidth: 315 }}>
            <div className="auth-form center-items">
              <h4>
                <FormattedMessage defaultMessage="Sign In" />
              </h4>
              {renderCountrySwitch()}
              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {/* Email Field */}
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: (
                      <FormattedMessage defaultMessage="Please enter a valid email" />
                    ),
                    pattern: {
                      value: /^[À-ÿA-Z0-9._%+-]+@[À-ÿA-Z0-9.-]+\.[A-Z]{2,18}$/i,
                      message: (
                        <FormattedMessage defaultMessage="Please enter a valid email" />
                      ),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      label={<FormattedMessage defaultMessage="email" />}
                      helperText={errors.email?.message}
                      error={!!errors.email}
                    />
                  )}
                />

                {/* Password Field */}
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: (
                      <FormattedMessage defaultMessage="Enter password!" />
                    ),
                  }}
                  render={({ field }) => (
                    <PasswordField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      label={<FormattedMessage defaultMessage="password" />}
                      helperText={errors.password?.message}
                      error={!!errors.password}
                      style={{ width: 256 }}
                    />
                  )}
                />

                <br />
                <div className="row center-items">
                  {renderAlert()}
                  {renderSignInButton()}
                </div>
                <br />
                <div className="row center-items">
                  <Link to="/forgot_password">
                    <p>
                      <strong>
                        <FormattedMessage defaultMessage="Forgot my password" />
                      </strong>
                    </p>
                  </Link>
                </div>
                <LocaleSelectMini />
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

SignIn.propTypes = {
  authenticated: PropTypes.bool,
  two_factor: PropTypes.bool,
  signinUser: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  setAppLocale: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  locale: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  return {
    locale: state.misc.appLocale,
    authenticated: state.auth.authenticated,
    two_factor: state.auth.two_factor,
    errorMessage: state.auth.error,
  };
}

export default connect(mapStateToProps, {
  signinUser,
  fetchUser,
  updateUser,
  setAppLocale,
})(SignIn);
/* eslint-enable no-shadow */
